<script setup lang="ts">
import {
  addScalarClassesToHeadless,
  ScalarTeleportRoot,
  type ModalState,
} from '@scalar/components'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import {
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  useId,
  watch,
} from 'vue'

const props = defineProps<{ modalState: ModalState }>()
const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
}>()

const client = ref<HTMLElement | null>(null)
const id = useId()

const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } =
  useFocusTrap(client, {
    allowOutsideClick: true,
    fallbackFocus: `#${id}`,
  })

// ensure scalar classes exist on headless-ui root
onBeforeMount(() => addScalarClassesToHeadless())

// manage the focus trap and emit lifecycle events
watch(
  () => props.modalState.open,
  async (open) => {
    if (open) {
      await nextTick()
      activateFocusTrap()
      emit('open')
    } else {
      deactivateFocusTrap()
      emit('close')
    }
  },
  { immediate: false },
)

// best-effort cleanup on unmount
onBeforeUnmount(() => {
  deactivateFocusTrap()
})
</script>

<template>
  <Transition name="scalar-client-fade">
    <div
      v-show="modalState.open"
      class="scalar scalar-app z-overlay relative">
      <div class="scalar-container">
        <div
          :id="id"
          ref="client"
          aria-label="API Client"
          aria-modal="true"
          v-bind="$attrs"
          class="scalar-app-layout scalar-client"
          role="dialog"
          tabindex="-1">
          <ScalarTeleportRoot>
            <slot />
          </ScalarTeleportRoot>
        </div>

        <!-- overlay / exit area -->
        <div
          class="scalar-app-exit"
          @click="modalState.hide()" />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@reference "@/style.css";

.scalar .scalar-app-layout {
  background: var(--scalar-background-1);
  height: calc(100% - 120px);
  max-width: 1390px;
  width: 100%;
  margin: auto;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: var(--scalar-border-width) solid var(--scalar-border-color);
}
/**
 * Allow the modal to fill more space on
 * very short (or very zoomed in) screens
 */
@variant zoomed {
  .scalar .scalar-app-layout {
    height: 100%;
    max-height: 90svh;
  }
}

.scalar .scalar-app-exit {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #00000038;
  cursor: pointer;
  z-index: -1;
}

.dark-mode .scalar .scalar-app-exit {
  background: rgba(0, 0, 0, 0.45);
}

.scalar .scalar-app-exit:before {
  font-family: sans-serif;
  position: absolute;
  top: 0;
  right: 12px;
  font-size: 30px;
  font-weight: 100;
  line-height: 50px;
  text-align: center;
  color: white;
  opacity: 0.6;
}

.scalar .scalar-app-exit:hover:before {
  opacity: 1;
}

/* container */
.scalar-container {
  overflow: hidden;
  visibility: visible;
  position: fixed;
  bottom: 0;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scalar .url-form-input {
  min-height: auto !important;
}

.scalar .scalar-container {
  line-height: normal;
}

.scalar-client-fade-enter-active,
.scalar-client-fade-leave-active {
  transition: opacity 0.35s ease;
}

.scalar-client-fade-enter-from,
.scalar-client-fade-leave-to {
  opacity: 0;
}
</style>
